import React, { useEffect, useMemo, useRef, useState } from "react";
import type { Match } from "../../../shared/types";

import Timeline from "./Timeline";
import { buildTimeline, formatClock } from "./replay-timeline";
import ReplayGameHandler from "./replay-game-handler.js";
import Score from "../game/Score";
import GameHud from "../game/GameHud";
import { matchPlaystyle } from "../../setup/presets";
// TEMP: power-up side boxes removed; see LocalGame note. Re-enable when the
// replacement (rings around the head) lands.
// import EffectsHud from "../game/EffectsHud";
import GameLayout from "../game/GameLayout";
import GameCanvas from "../../../game-canvas/GameCanvas";
import musicController from "../../../audio/music-controller";
import * as replay from "../../../shared/replay-reconstruction.js";
import * as windowFocusHandler from "../../../shared/window-focus-handler.js";
import { parseEvent, CONTINUE_KEY } from "../../../shared/key-util.js";
import Paused from "../../../game-canvas/overlays/Paused";
import Countdown from "../../../game-canvas/overlays/Countdown";
import styles from "../play.module.scss";
import { roundWinners } from "../../../shared/round-winners";

interface Props {
  match: Match;
  roundId: number;
  // Threaded in by App but currently unused here (GameCanvas doesn't read it).
  overlay?: any;
  onReplayOver: () => void;
  // For a viewer that studies a round (the admin replay page): extra drawing over
  // the arena, and a handle to jump to a moment and hold there.
  arenaOverlay?: React.ReactNode;
  controlRef?: React.MutableRefObject<{ seekTo: (gameTime: number) => void } | null>;
  // A viewer of a whole match: something between Pause and the scrubber (its round
  // switcher), and a call when playback runs off the end of this round.
  footerExtra?: React.ReactNode;
  onRoundPlayedOut?: () => void;
  // The viewer's own seat(s), when known: their crash is raised on the timeline.
  youIds?: string[];
}

const SPEEDS = [0.5, 1, 2, 4];
let rememberedSpeed = 1;
// A hop lands a beat before the event, so you see it happen rather than its aftermath.
const HOP_LEAD = 1.2;

// "0:15 / 0:25", ticking on its own so the whole player need not re-render for it.
function ReplayClock({ getTime, duration }: { getTime: () => number; duration: number }) {
  const [now, setNow] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setNow(getTime()), 200);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <span className={styles.replayClock}>{formatClock(now)} / {formatClock(duration)}</span>;
}

// Watching a recorded round. It renders through the SAME shell and components as
// a live round (GameLayout → HUD / EffectsHud / toolbar / Score / GameCanvas) and
// plays the same adaptive music + SFX — the only differences are the scrubber +
// pause under the arena and that the toolbar's leave button exits the replay
// straight away. Everything time-dependent reads `replayGame.getReplayTime`, and
// because a replay state has no live `.alive` / `.effects` snapshot, the HUD,
// effects panel and audio reconstruct their data from the round's event logs at
// that time (see shared/replay-reconstruction).
export default function Replay({ match, roundId, onReplayOver, arenaOverlay, controlRef, footerExtra, onRoundPlayedOut, youIds }: Props) {
  const roundData = match.matchState.roundsData[roundId];
  const gameState = roundData.gameState;
  const players = match.matchConfig.players;

  const [, setTick] = useState(0);
  const forceRender = () => setTick((t) => t + 1);

  const onReplayOverRef = useRef(onReplayOver);
  onReplayOverRef.current = onReplayOver;

  const onRoundPlayedOutRef = useRef(onRoundPlayedOut);
  onRoundPlayedOutRef.current = onRoundPlayedOut;

  const [replayGame] = useState(() =>
    ReplayGameHandler({
      gameState,
      onReplayOver: () => onReplayOverRef.current(),
      // Re-render when the replay reaches the end so the button flips to "Restart"
      // even though nothing else triggers a render at that moment.
      onEnded: () => { forceRender(); if (onRoundPlayedOutRef.current) onRoundPlayedOutRef.current(); },
    })
  );

  // What happened in the round, for the scrubber (crashes, pickups, the countdown).
  const timeline = useMemo(() => buildTimeline(gameState, players, youIds || []), [gameState, players, youIds]);
  // The speed is the viewer's, not the round's: it carries over when the round changes
  // (each round is a fresh player), so it is remembered here, outside any one of them.
  const [speed, setSpeedState] = useState(() => { replayGame.setSpeed(rememberedSpeed); return rememberedSpeed; });
  function setSpeed(next: number) {
    rememberedSpeed = next;
    replayGame.setSpeed(next);
    setSpeedState(next);
  }
  // Previous / next event. Play state is kept: hopping while it plays keeps playing.
  function hop(direction: 1 | -1) {
    const now = replayGame.getReplayTime();
    const times = timeline.marks.map((m) => m.time);
    const target = direction > 0
      ? times.find((t) => t - HOP_LEAD > now + 0.05)
      : [...times].reverse().find((t) => t - HOP_LEAD < now - 0.6);
    if (target === undefined) return;
    replayGame.setReplayProgress(Math.max(0, target - HOP_LEAD) / (gameState.gameTime || 1));
    forceRender();
  }

  // Logic-only flag (never rendered directly), so a ref rather than state.
  const pausedDueToLostFocusRef = useRef(false);

  // Pause / Resume, and Restart once the replay has run to the end.
  function pauseButtonAction() {
    if (replayGame.isEnded()) {
      replayGame.restart();
    } else if (replayGame.isPaused()) {
      replayGame.resume();
    } else {
      replayGame.pause();
    }
    forceRender();
  }

  // Grabbing the scrubber pauses the replay (and it stays paused after the seek,
  // so you can study a moment); hit Resume to continue from there.
  function sliderPause() {
    if (!replayGame.isPaused()) {
      replayGame.pause();
      forceRender();
    }
  }
  function sliderSeek(progress: number) {
    replayGame.setReplayProgress(progress);
    forceRender();
  }

  if (controlRef) {
    controlRef.current = {
      seekTo: (gameTime: number) => {
        replayGame.pause();
        replayGame.setReplayProgress(Math.max(0, Math.min(1, gameTime / (gameState.gameTime || 1))));
        forceRender();
      },
    };
  }

  useEffect(() => {
    function onWindowFocus() {
      if (pausedDueToLostFocusRef.current) {
        replayGame.resume();
        pausedDueToLostFocusRef.current = false;
      }
    }
    function onWindowBlur() {
      if (!replayGame.isPaused()) {
        replayGame.pause();
        pausedDueToLostFocusRef.current = true;
      }
    }
    function onKeyUp(event: KeyboardEvent) {
      if (parseEvent(event) === CONTINUE_KEY) {
        pauseButtonAction();
      }
    }
    replayGame.start();
    // Drive the same adaptive music / SFX as a live round, but from the round's
    // logs at the current replay time (null once the replay is exited so the
    // track fades and the SFX counters reset). While paused — which includes
    // scrubbing, since grabbing the bar pauses — suppress SFX so seeking past
    // crashes/pickups doesn't fire them; the music keeps playing.
    musicController.setGameAudioProvider(() => {
      if (replayGame.isReplayOver()) {
        return null;
      }
      const audio = replay.getReplayAudioState(gameState, replayGame.getReplayTime());
      audio.suppressSfx = replayGame.isPaused();
      return audio;
    });
    windowFocusHandler.on("focus", onWindowFocus);
    windowFocusHandler.on("blur", onWindowBlur);
    document.addEventListener("keyup", onKeyUp);
    return () => {
      replayGame.stop();
      musicController.setGameAudioProvider(null);
      document.removeEventListener("keyup", onKeyUp);
      windowFocusHandler.off("focus", onWindowFocus);
      windowFocusHandler.off("blur", onWindowBlur);
    };
  }, []);

  const getRenderTime = replayGame.getReplayTime;

  const arena = (
    <GameCanvas
      config={{ fullscreen: true, spectator: true }}
      gameState={gameState}
      players={players}
      renderTime={getRenderTime}
    >
      {replayGame.isPaused() ? <Paused gameState={gameState} /> : null}
      <Countdown gameState={gameState} getRenderTime={() => replayGame.getReplayTime()} />
      {arenaOverlay}
    </GameCanvas>
  );

  const pauseLabel = replayGame.isEnded() ? "Restart" : replayGame.isPaused() ? "Resume" : "Pause";
  const pauseButton = (
    <button className={styles.replayPauseBtn} onClick={pauseButtonAction}>
      {pauseLabel}
    </button>
  );
  const exitButton = (
    <button className={styles.replayExitBtn} onClick={replayGame.stop}>
      Exit
    </button>
  );
  const progressBar = (
    <Timeline
      timeline={timeline}
      progress={replayGame.getReplayProgress}
      onScrubStart={sliderPause}
      onSeek={sliderSeek}
    />
  );
  const eventButtons = timeline.marks.length > 0 ? (
    <div className={styles.replayHop}>
      <button onClick={() => hop(-1)} title="Previous event" aria-label="Previous event">‹ Event</button>
      <button onClick={() => hop(1)} title="Next event" aria-label="Next event">Event ›</button>
    </div>
  ) : null;
  const speedButtons = (
    <div className={styles.replaySpeed} role="group" aria-label="Playback speed">
      {SPEEDS.map((v) => (
        <button key={v} className={v === speed ? styles.replaySpeedOn : undefined} onClick={() => setSpeed(v)} aria-pressed={v === speed}>{v}×</button>
      ))}
    </div>
  );

  const score = (
    <Score
      gameState={gameState}
      players={players}
      renderTime={getRenderTime}
      startScore={roundData.startScore}
      maxScore={match.matchConfig.maxScore}
      maxRounds={match.matchConfig.maxRounds}
      live={true}
    />
  );

  return (
    <GameLayout
      hud={
        <GameHud
          gameState={gameState}
          roundNumber={roundId + 1}
          totalPlayers={players.length}
          maxScore={match.matchConfig.maxScore}
          maxRounds={match.matchConfig.maxRounds}
          renderTime={getRenderTime}
          players={players}
          winners={roundWinners(match.matchState.roundsData.slice(0, roundId))}
        />
      }
      arena={arena}
      arenaFooterHeight={116}
      arenaFooter={
        <div className={styles.replayControls}>
          <div className={styles.replayRow}>
            {pauseButton}
            {footerExtra}
            {eventButtons}
            {speedButtons}
            <ReplayClock getTime={replayGame.getReplayTime} duration={gameState.gameTime} />
            {exitButton}
          </div>
          <div className={styles.replayBar}>{progressBar}</div>
        </div>
      }
      score={score}
      map={gameState.map}
      playstyle={matchPlaystyle(match.matchConfig)}
      onLeave={replayGame.stop}
      confirmLeave={false}
    />
  );
}
